import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { recipes } from '../lib/api';

const ALL_CATEGORIES = [
  'appetizer', 'main-course', 'side-dish', 'dessert', 'snack',
  'beverage', 'sauce', 'soup', 'salad', 'bread', 'breakfast',
  'quick-meal', 'one-pot', 'meal-prep', 'pasta', 'seafood',
  'poultry', 'beef', 'pork', 'vegetable'
];

const ALL_CUISINES = [
  'Italian', 'Mexican', 'Thai', 'Chinese', 'Japanese', 'Indian',
  'American', 'French', 'Mediterranean', 'Korean', 'Vietnamese',
  'Middle-Eastern', 'Greek', 'Spanish', 'British', 'German',
  'African', 'Caribbean', 'Brazilian', 'Fusion'
];

const ALL_DIETARY = [
  'vegetarian', 'vegan', 'gluten-free', 'dairy-free', 'keto',
  'low-carb', 'paleo', 'nut-free', 'kosher', 'halal',
  'pescatarian', 'whole30'
];

const formatTag = (tag) => tag.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

function TagChip({ tag, type, isAI, isSelected, onClick, disabled }) {
  const colorClasses = {
    category: isAI ? 'bg-accent-50 text-accent-600 border-dashed border-accent-300 dark:bg-accent-900/20 dark:text-accent-400 dark:border-accent-700 hover:bg-accent-100 dark:hover:bg-accent-900/30' :
      isSelected ? 'bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400 border-accent-300' :
      'bg-white text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-300 hover:border-accent-300',
    cuisine: isAI ? 'bg-blue-50 text-blue-600 border-dashed border-blue-300 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30' :
      isSelected ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-300' :
      'bg-white text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-300 hover:border-blue-300',
    dietary: isAI ? 'bg-green-50 text-green-600 border-dashed border-green-300 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/30' :
      isSelected ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-300' :
      'bg-white text-gray-600 dark:bg-gray-800 dark:text-gray-400 border-gray-300 hover:border-green-300',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${colorClasses[type]} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
    >
      {formatTag(tag)}
      {isSelected && !isAI && (
        <span className="ml-1.5">&times;</span>
      )}
    </button>
  );
}

// Action menu for AI tags
function AITagActionMenu({ tag, type, onAccept, onRemove, onChange, onClose }) {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className="absolute z-10 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 min-w-[140px]"
      style={{ top: '100%', left: 0 }}
    >
      <button
        onClick={() => { onAccept(tag, type); onClose(); }}
        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
      >
        <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Accept
      </button>
      <button
        onClick={() => { onRemove(tag, type); onClose(); }}
        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
      >
        <svg className="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
        Remove
      </button>
      <button
        onClick={() => { onChange(tag, type); onClose(); }}
        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
      >
        <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 1l4 4-4 4" />
          <path d="M3 11V9a4 4 0 0 1 4-4h14" />
          <path d="M7 23l-4-4 4-4" />
          <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
        Change to...
      </button>
    </div>
  );
}

// Tag picker for changing AI tags
function TagPicker({ type, currentTag, allTags, onSelect, onClose }) {
  const pickerRef = useRef(null);
  const availableTags = allTags.filter(t => t.toLowerCase() !== currentTag.toLowerCase());

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={pickerRef}
      className="absolute z-20 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 max-w-xs max-h-60 overflow-y-auto"
      style={{ top: '100%', left: 0 }}
    >
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        Replace "{formatTag(currentTag)}" with:
      </p>
      <div className="flex flex-wrap gap-1.5">
        {availableTags.map(tag => (
          <button
            key={tag}
            onClick={() => onSelect(tag)}
            className="px-2 py-1 text-xs rounded-full border border-gray-300 dark:border-gray-600 hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20"
          >
            {formatTag(tag)}
          </button>
        ))}
      </div>
    </div>
  );
}

function TagSection({ title, allTags, aiTags, selectedTags, onToggle, onAITagClick, type, activeMenu, tagPickerState }) {
  const [expanded, setExpanded] = useState(false);
  const availableTags = allTags.filter(t => !aiTags.includes(t) && !selectedTags.includes(t));

  return (
    <div className="space-y-3">
      <h4 className="font-medium text-gray-900 dark:text-white">{title}</h4>

      {/* AI Tags */}
      {aiTags.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            AI-detected <span className="text-gray-400">(click to manage)</span>
          </p>
          <div className="flex flex-wrap gap-2">
            {aiTags.map(tag => (
              <div key={tag} className="relative">
                <TagChip
                  tag={tag}
                  type={type}
                  isAI
                  onClick={(e) => onAITagClick(tag, type, e)}
                />
                {activeMenu?.tag === tag && activeMenu?.type === type && activeMenu.menu}
                {tagPickerState?.tag === tag && tagPickerState?.type === type && tagPickerState.picker}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Tags */}
      {selectedTags.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Your tags</p>
          <div className="flex flex-wrap gap-2">
            {selectedTags.map(tag => (
              <TagChip
                key={tag}
                tag={tag}
                type={type}
                isSelected
                onClick={() => onToggle(tag)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Add more */}
      <div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1"
        >
          {expanded ? '− Hide options' : '+ Add more'}
        </button>

        {expanded && (
          <div className="mt-3 flex flex-wrap gap-2">
            {availableTags.length === 0 ? (
              <p className="text-sm text-gray-500">All tags selected</p>
            ) : (
              availableTags.map(tag => (
                <TagChip
                  key={tag}
                  tag={tag}
                  type={type}
                  onClick={() => onToggle(tag)}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TagEditor({ recipeId, isOpen, onClose }) {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [aiTags, setAiTags] = useState({ categories: [], cuisines: [], dietary: [] });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [selectedDietary, setSelectedDietary] = useState([]);

  // State for AI tag action menu
  const [activeMenu, setActiveMenu] = useState(null);
  const [tagPickerState, setTagPickerState] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadTags();
    }
  }, [isOpen, recipeId]);

  const loadTags = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await recipes.getTags(recipeId);
      setAiTags(data.aiTags);
      setSelectedCategories(data.userTags.categories);
      setSelectedCuisines(data.userTags.cuisines);
      setSelectedDietary(data.userTags.dietary);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const saveMutation = useMutation({
    mutationFn: () => recipes.updateTags(recipeId, {
      categories: selectedCategories,
      cuisines: selectedCuisines,
      dietary: selectedDietary,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      onClose();
    },
  });

  // Mutation for removing AI tags
  const removeAiTagMutation = useMutation({
    mutationFn: ({ tag, type }) => recipes.deleteAiTag(recipeId, type, tag),
    onSuccess: (_, { tag, type }) => {
      const typeMap = { category: 'categories', cuisine: 'cuisines', dietary: 'dietary' };
      const key = typeMap[type];
      setAiTags(prev => ({
        ...prev,
        [key]: prev[key].filter(t => t !== tag)
      }));
      queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });

  // Mutation for accepting AI tags
  const acceptAiTagMutation = useMutation({
    mutationFn: ({ tag, type }) => recipes.acceptAiTag(recipeId, type, tag),
    onSuccess: (_, { tag, type }) => {
      const typeMap = { category: 'categories', cuisine: 'cuisines', dietary: 'dietary' };
      const key = typeMap[type];
      setAiTags(prev => ({
        ...prev,
        [key]: prev[key].filter(t => t !== tag)
      }));
      if (type === 'category') {
        setSelectedCategories(prev => [...prev, tag]);
      } else if (type === 'cuisine') {
        setSelectedCuisines(prev => [...prev, tag]);
      } else if (type === 'dietary') {
        setSelectedDietary(prev => [...prev, tag]);
      }
      queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });

  const toggleCategory = (tag) => {
    setSelectedCategories(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const toggleCuisine = (tag) => {
    setSelectedCuisines(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const toggleDietary = (tag) => {
    setSelectedDietary(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleAITagClick = (tag, type, event) => {
    event.stopPropagation();
    setTagPickerState(null);
    setActiveMenu({
      tag,
      type,
      menu: (
        <AITagActionMenu
          tag={tag}
          type={type}
          onAccept={(t, ty) => acceptAiTagMutation.mutate({ tag: t, type: ty })}
          onRemove={(t, ty) => removeAiTagMutation.mutate({ tag: t, type: ty })}
          onChange={(t, ty) => {
            setActiveMenu(null);
            const allTagsMap = { category: ALL_CATEGORIES, cuisine: ALL_CUISINES, dietary: ALL_DIETARY };
            setTagPickerState({
              tag: t,
              type: ty,
              picker: (
                <TagPicker
                  type={ty}
                  currentTag={t}
                  allTags={allTagsMap[ty]}
                  onSelect={(newTag) => {
                    removeAiTagMutation.mutate({ tag: t, type: ty });
                    if (ty === 'category') {
                      setSelectedCategories(prev => [...prev, newTag]);
                    } else if (ty === 'cuisine') {
                      setSelectedCuisines(prev => [...prev, newTag]);
                    } else if (ty === 'dietary') {
                      setSelectedDietary(prev => [...prev, newTag]);
                    }
                    setTagPickerState(null);
                  }}
                  onClose={() => setTagPickerState(null)}
                />
              )
            });
          }}
          onClose={() => setActiveMenu(null)}
        />
      )
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Tags</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500 mb-4">{error}</p>
              <button onClick={loadTags} className="btn btn-secondary">Try Again</button>
            </div>
          ) : (
            <>
              <TagSection
                title="Categories"
                allTags={ALL_CATEGORIES}
                aiTags={aiTags.categories}
                selectedTags={selectedCategories}
                onToggle={toggleCategory}
                onAITagClick={handleAITagClick}
                type="category"
                activeMenu={activeMenu}
                tagPickerState={tagPickerState}
              />

              <TagSection
                title="Cuisines"
                allTags={ALL_CUISINES}
                aiTags={aiTags.cuisines}
                selectedTags={selectedCuisines}
                onToggle={toggleCuisine}
                onAITagClick={handleAITagClick}
                type="cuisine"
                activeMenu={activeMenu}
                tagPickerState={tagPickerState}
              />

              <TagSection
                title="Dietary"
                allTags={ALL_DIETARY}
                aiTags={aiTags.dietary}
                selectedTags={selectedDietary}
                onToggle={toggleDietary}
                onAITagClick={handleAITagClick}
                type="dietary"
                activeMenu={activeMenu}
                tagPickerState={tagPickerState}
              />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || isLoading}
            className="btn btn-primary"
          >
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Compact tag display for recipe detail page
export function RecipeTagsDisplay({ recipe, onEditClick }) {
  const hasCategories = recipe.categories?.length > 0;
  const hasCuisines = recipe.cuisines?.length > 0;
  const hasDietary = recipe.dietary?.length > 0;

  if (!hasCategories && !hasCuisines && !hasDietary) {
    return null;
  }

  return (
    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">Tags</h4>
        <button
          onClick={onEditClick}
          className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          Edit
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {recipe.categories?.map(tag => (
          <span key={`cat-${tag}`} className="px-2.5 py-1 bg-accent-50 text-accent-600 border border-dashed border-accent-300 dark:bg-accent-900/20 dark:text-accent-400 dark:border-accent-700 rounded-full text-xs">
            {formatTag(tag)}
          </span>
        ))}
        {recipe.cuisines?.map(tag => (
          <span key={`cui-${tag}`} className="px-2.5 py-1 bg-blue-50 text-blue-600 border border-dashed border-blue-300 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-700 rounded-full text-xs">
            {formatTag(tag)}
          </span>
        ))}
        {recipe.dietary?.map(tag => (
          <span key={`diet-${tag}`} className="px-2.5 py-1 bg-green-50 text-green-600 border border-dashed border-green-300 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700 rounded-full text-xs">
            {formatTag(tag)}
          </span>
        ))}
      </div>
    </div>
  );
}
